import { useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import type { Message } from '@orbit-sync/shared-types';
import { dirFor } from '../chat/direction.js';
import { useSettingsStore } from '../settings/settingsStore.js';
import { useSocket } from '../socket/SocketProvider.js';
import { Icon } from './Icon.js';

const MAX_TEXTAREA_PX = 220;
const ACK_TIMEOUT_MS = 10_000;

/**
 * Inline editor for one of your own messages, in place of its bubble.
 *
 * Enter saves, Shift+Enter is a newline, Esc cancels - the composer's keys.
 * The new text appears through the server's `chat.messageEdited` broadcast,
 * which lands before the acknowledgement, so the editor just closes on success.
 */
export function MessageEditor({ message, onDone }: { message: Message; onDone: () => void }) {
  const { socket, status } = useSocket();
  const maxLength = useSettingsStore((state) => state.settings.maxMessageLength);
  const [content, setContent] = useState(message.content);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const element = ref.current;

    if (!element) {
      return;
    }

    element.style.height = 'auto';
    element.style.height = `${Math.min(element.scrollHeight, MAX_TEXTAREA_PX)}px`;
  }, [content]);

  // Caret at the end, the way people expect to resume typing.
  useLayoutEffect(() => {
    const element = ref.current;
    element?.focus();
    element?.setSelectionRange(element.value.length, element.value.length);
  }, []);

  const trimmed = content.trim();
  const tooLong = trimmed.length > maxLength;
  const canSave = !busy && trimmed.length > 0 && !tooLong && status === 'connected';

  async function save(): Promise<void> {
    if (!canSave || !socket) {
      return;
    }

    if (trimmed === message.content) {
      onDone();
      return;
    }

    setBusy(true);
    setError(null);

    try {
      const result = await socket.timeout(ACK_TIMEOUT_MS).emitWithAck('chat.edit', {
        channelId: message.channelId,
        messageId: message.id,
        content: trimmed,
      });

      if (result.ok) {
        onDone();
        return;
      }

      setError(result.message);
    } catch {
      setError('No answer from the server. Try again.');
    }

    setBusy(false);
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      onDone();
    } else if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void save();
    }
  }

  return (
    <div className="msg-editor">
      <textarea
        ref={ref}
        value={content}
        dir={dirFor(content)}
        aria-label="Edit message"
        disabled={busy}
        onChange={(event) => setContent(event.target.value)}
        onKeyDown={onKeyDown}
      />
      <div className="msg-editor-foot">
        <span className={tooLong ? 'msg-editor-count msg-editor-count--over' : 'msg-editor-count'}>
          {error ?? (tooLong ? `${trimmed.length}/${maxLength}` : 'Enter to save · Esc to cancel')}
        </span>
        <button type="button" className="btn btn--ghost btn--sm" onClick={onDone} disabled={busy}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn--primary btn--sm"
          onClick={() => void save()}
          disabled={!canSave}
        >
          {busy ? <span className="btn-spinner" /> : <Icon name="check" size={13} />}
          Save
        </button>
      </div>
    </div>
  );
}
